import { useState, useEffect } from "react";
import {
  Search, Eye, Download, FileText, Loader2, RefreshCw, X,
} from "lucide-react";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../../components/ui/card";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { toast } from "../../../utils/toast";
import axios from "axios";
import { API_URL } from "../../../api/api";
import { LETTER_TYPES } from "../../../utils/letterTemplates";
import { LetterPreview } from "./LetterPreview";

export function LetterLibrary() {
  const [letters, setLetters]               = useState([]);
  const [filteredLetters, setFilteredLetters] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchQuery, setSearchQuery]       = useState("");
  const [filterType, setFilterType]         = useState("all");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showPreview, setShowPreview]       = useState(false);
  const [companyData]                       = useState(
    JSON.parse(localStorage.getItem("companyData") || "null")
  );

  useEffect(() => { fetchLetters(); }, []);
  useEffect(() => { filterList(); }, [searchQuery, filterType, letters]);

  const fetchLetters = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/generated-letters`);
      const rows = Array.isArray(data) ? data : [];
      // Normalise placeholder_data
      const parsed = rows.map((r) => ({
        ...r,
        placeholder_data:
          r.placeholder_data && typeof r.placeholder_data === "string"
            ? JSON.parse(r.placeholder_data)
            : r.placeholder_data || {},
      }));
      setLetters(parsed);
    } catch (err) {
      console.error("Error fetching generated letters:", err);
      toast.error("Could not load letter library");
    } finally {
      setLoading(false);
    }
  };

  const filterList = () => {
    let list = [...letters];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (l) =>
          l.subject?.toLowerCase().includes(q) ||
          l.placeholder_data?.employee_name?.toLowerCase().includes(q) ||
          l.placeholder_data?.employeeName?.toLowerCase().includes(q) ||
          String(l.id).includes(q)
      );
    }
    if (filterType !== "all") {
      list = list.filter((l) => l.template_type === filterType);
    }
    setFilteredLetters(list);
  };

  const openPreview = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/generated-letters/${id}`);
      const letter = {
        ...data,
        placeholder_data:
          data.placeholder_data && typeof data.placeholder_data === "string"
            ? JSON.parse(data.placeholder_data)
            : data.placeholder_data || {},
      };
      setSelectedLetter(letter);
      setShowPreview(true);
    } catch (err) {
      console.error("Error fetching letter:", err);
      toast.error("Failed to load letter");
    }
  };

  const getTypeLabel = (type) =>
    LETTER_TYPES.find((t) => t.value === type)?.label || type;

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
          year: "numeric", month: "short", day: "numeric",
        })
      : "—";

  const statusClass = (s) => ({
    generated : "lt-badge--blue",
    sent      : "lt-badge--green",
    draft     : "lt-badge--gray",
  }[s] || "lt-badge--gray");

  if (loading) {
    return (
      <div className="lt-loading">
        <Loader2 className="lt-spinner" />
        <span>Loading letters…</span>
      </div>
    );
  }

  return (
    <div className="lt-library">
      {/* Search + Filter bar */}
      <Card className="lt-card">
        <CardContent className="lt-search-bar">
          <div className="lt-search-input-wrap">
            <Search size={15} className="lt-search-icon" />
            <input
              className="lt-input lt-search-input"
              placeholder="Search by subject, employee name, or ID…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="lt-clear-btn" onClick={() => setSearchQuery("")}>
                <X size={14} />
              </button>
            )}
          </div>
          <select
            className="lt-input lt-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Letter Types</option>
            {LETTER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button className="lt-btn-ghost" onClick={fetchLetters} title="Refresh">
            <RefreshCw size={15} />
          </button>
        </CardContent>
      </Card>

      {/* Letters table */}
      <Card className="lt-card">
        <CardHeader>
          <CardTitle>
            Generated Letters&nbsp;
            <span className="lt-count-badge">{filteredLetters.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLetters.length === 0 ? (
            <div className="lt-empty-state">
              <FileText size={48} className="lt-empty-icon" />
              <p>
                {searchQuery || filterType !== "all"
                  ? "No letters match your search criteria"
                  : "No letters generated yet. Use 'Generate Letter' to create your first one."}
              </p>
            </div>
          ) : (
            <div className="lt-table-wrap">
              <table className="lt-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Subject</th>
                    <th>Employee</th>
                    <th>Status</th>
                    <th className="lt-th-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLetters.map((letter) => (
                    <tr key={letter.id} className="lt-tr">
                      <td className="lt-td-date">{formatDate(letter.created_at)}</td>
                      <td>
                        <span className="lt-type-badge">{getTypeLabel(letter.template_type)}</span>
                      </td>
                      <td className="lt-td-subject" title={letter.subject}>{letter.subject}</td>
                      <td>
                        {letter.placeholder_data?.employee_name ||
                          letter.placeholder_data?.employeeName || "—"}
                      </td>
                      <td>
                        <span className={`lt-status-badge ${statusClass(letter.status)}`}>
                          {letter.status || "generated"}
                        </span>
                      </td>
                      <td className="lt-td-actions">
                        <button
                          className="lt-icon-btn"
                          title="Preview"
                          onClick={() => openPreview(letter.id)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="lt-icon-btn"
                          title="Quick preview"
                          onClick={() => { setSelectedLetter(letter); setShowPreview(true); }}
                        >
                          <Download size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      {showPreview && selectedLetter && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="lt-preview-dialog">
            <DialogHeader>
              <DialogTitle>Letter Preview</DialogTitle>
              <DialogDescription>View, print, or download the letter</DialogDescription>
            </DialogHeader>
            <LetterPreview
              letter={selectedLetter}
              companyData={companyData}
              onClose={() => setShowPreview(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
